import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { TagDateValue, IValueViewModel, SeriesDefinition, ComponentDefinition, RefreshClickData } from '../../../models';
import { ChartConfigurationOption } from '../../../wtFramework';
import { IIdentifierVmService, IGroupService, IIdentifierDetailsService, IValuesService, IDateRangeService, IExportToCsvService } from '../../../services';
import { refreshClick } from '../../../common/events';
import { momentFormat } from '../../../common/constants';

interface StateParams extends angular.ui.IStateParamsService {
    beginDate?: string;
    endDate?: string;
    identifierGuid: string;
}

class TargetGeneralController implements angular.IComponentController {
    data: TagDateValue[];
    identifierGuid: string;
    identifierVm: IValueViewModel;
    initialRange: { beginDate: string; endDate: string; };
    inputValuesQuality: { configuration: { type: string; guid: string; }; };
    isLoading: boolean;
    isLoadingData: boolean;
    loadingMessage: string;
    options: ChartConfigurationOption;
    series: SeriesDefinition<TagDateValue>[];

    private _seriesConfiguration: { guid: string; value: string; }[];

    static $inject = [
        '$scope',
        '$stateParams',
        'identifierDetailsService',
        'groupService',
        'identifierVmService',
        'valuesService', 
        'dateRangeService', 
        'exportToCsvService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$stateParams: StateParams,
        private _identifierDetailsService: IIdentifierDetailsService,
        private _groupService: IGroupService,
        private _ivms: IIdentifierVmService,
        private _valuesService: IValuesService,
        private _dateRangeService: IDateRangeService,
        private _exportToCsvService: IExportToCsvService) {
    }

    $onInit() {
        const { identifierGuid, beginDate, endDate } = this._$stateParams;
        this.identifierGuid = identifierGuid;
        if (beginDate && endDate) {
            this.initialRange = {
                beginDate: moment(beginDate).format(momentFormat),
                endDate: moment(endDate).format(momentFormat)
            };
        }
        this.data = [];
        this.options = this._identifierDetailsService.getChartConfiguration();
        this.inputValuesQuality = { configuration: { type: 'identifier', guid: this.identifierGuid } };

        this.isLoading = true;
        this.loadingMessage = 'Retrieving identifier';
        this._groupService.getIdentifier(this.identifierGuid)
            .then(_data => {
                return this._ivms.getSomeViewModels([this.identifierGuid]);
            })
            .then(data => {
                this.identifierVm = data[0];
                this._seriesConfiguration = [
                    {
                        guid: this.identifierVm.guid,
                        value: 'sourcevalue'
                    },
                    {
                        guid: this.identifierVm.guid,
                        value: 'targetvalue'
                    }
                ];
                this.series = this._identifierDetailsService.getSeries(this.identifierVm);
                let beginDate, endDate;
                if (this.initialRange) {
                    beginDate = this.initialRange.beginDate;
                    endDate = this.initialRange.endDate;
                } else {
                    beginDate = this._dateRangeService.beginDate() as string;
                    endDate = this._dateRangeService.endDate() as string;
                }
                return this.refresh(beginDate, endDate);
            })
            .then(_data => {
                this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
                    this.refresh(data.beginDate, data.endDate, data.force);
                });
            })
            .finally(() => this.isLoading = false);

    }

    refresh(beginDate: string, endDate: string, force: boolean = false) {
        this.loadingMessage = 'Loading data';
        this.options.display.xMin = moment(beginDate).toDate();
        this.options.display.xMax = moment(endDate).toDate();
        this.isLoading = true;
        return this._valuesService.getValuesWithMissing(this._seriesConfiguration, beginDate, endDate, force)
            .then(data => {
                const { values } = data;
                const timestamps = _.uniq(values.map(d => d.timeStamp))
                    .sort((a, b) => a > b ? 1 : -1);
                const joined: TagDateValue[] = timestamps
                    .map(d => {
                        const sv = values.filter(v => v.timeStamp === d);
                        const source = sv.filter(v => v.valueType === 'sourcevalue');
                        const target = sv.filter(v => v.valueType === 'targetvalue');
                        const res: TagDateValue = {
                            timeStamp: d,
                            value: source.length ? source[0].value : null,
                            secondaryValue: target.length ? target[0].value : null,
                            guid: 'joined'
                        };
                        return res;
                    });
                // .filter(d => (d.value || d.value === 0) && (d.secondaryValue || d.secondaryValue === 0));
                const prepared = this._identifierDetailsService.prepareData(data, beginDate, endDate);
                this.data = prepared.values
                    .concat(joined);
            })
            .finally(() => this.isLoading = false);
    }

    exportCsv() {
        this.loadingMessage = 'Exporting data to csv';
        this.isLoading = true;
        this._exportToCsvService.getCsvFromData(this.mapToCsvFormat(this.data, this._seriesConfiguration), `Trend-${this.identifierVm.name}.csv`);
        this.isLoading = false;
    }

    private mapToCsvFormat(data: TagDateValue[], series: { guid: string, value: string }[]): string[][] {
        let result: string[][] = [];
        result.push(['Time stamp', this.identifierVm.name, this.identifierVm.name]);
        result.push(['', 'Source', 'Target']);
        const dates = _.uniq(data.filter(d => d.guid && d.guid !== 'joined').map(d => d.timeStamp));
        const mapped = dates.map(date => {
            const v = data.filter(d => d.timeStamp === date);
            const source = _.find(v, d => d.valueType === 'sourcevalue');
            const target = _.find(v, d => d.valueType === 'targetvalue');
            return [moment(date).format(momentFormat),
            source && source.value !== null ? source.value.toString() : '',
            target && target.value !== null ? target.value.toString() : ''];
        });
        result = result.concat(mapped);
        return result;
    }
}

export const wtTargetGeneral: ComponentDefinition = {
    componentId: 'wtTargetGeneral',
    controller: TargetGeneralController,
    template: require('./targetGeneral.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};